// Маленькие хуки доступа к данным поверх типизированного fetch-клиента.
// useFetch — декларативная загрузка GET-ресурса с состояниями loading/error.
// useLazyFetch — императивная загрузка по требованию (для форм отчётов).

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, type RequestOptions } from '@/api/client'

export interface FetchState<T> {
  data: T | null
  loading: boolean
  error: string | null
}

export function toMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Неизвестная ошибка'
}

/**
 * Декларативно загружает GET-ресурс при монтировании и при смене `deps`.
 * Если `path` равен null — запрос не выполняется (для условных загрузок).
 * Гонки запросов гасятся через AbortController + флаг актуальности.
 */
export interface FetchResult<T> extends FetchState<T> {
  /** Перезагрузить ресурс (например, после мутации). */
  reload: () => void
}

export function useFetch<T>(
  path: string | null,
  query?: RequestOptions['query'],
  deps: ReadonlyArray<unknown> = []
): FetchResult<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    loading: path !== null,
    error: null
  })
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (path === null) {
      setState({ data: null, loading: false, error: null })
      return
    }
    const ctrl = new AbortController()
    let active = true
    setState((s) => ({ ...s, loading: true, error: null }))
    api
      .get<T>(path, { query, signal: ctrl.signal })
      .then((data) => {
        if (active) setState({ data, loading: false, error: null })
      })
      .catch((err: unknown) => {
        if (!active || ctrl.signal.aborted) return
        setState({ data: null, loading: false, error: toMessage(err) })
      })
    return () => {
      active = false
      ctrl.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps])

  return { ...state, reload }
}

export interface LazyFetchState<T> extends FetchState<T> {
  /** Запустить запрос вручную. Возвращает данные или null при ошибке. */
  run: (path: string, query?: RequestOptions['query']) => Promise<T | null>
}

/**
 * Императивный вариант: запрос запускается явным вызовом `run`.
 * Удобно для инструментов отчётов, где загрузка идёт по кнопке «Построить».
 */
export function useLazyFetch<T>(): LazyFetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    loading: false,
    error: null
  })
  const ctrlRef = useRef<AbortController | null>(null)

  const run = useCallback(
    async (path: string, query?: RequestOptions['query']): Promise<T | null> => {
      ctrlRef.current?.abort()
      const ctrl = new AbortController()
      ctrlRef.current = ctrl
      setState({ data: null, loading: true, error: null })
      try {
        const data = await api.get<T>(path, { query, signal: ctrl.signal })
        if (!ctrl.signal.aborted) setState({ data, loading: false, error: null })
        return data
      } catch (err) {
        if (ctrl.signal.aborted) return null
        setState({ data: null, loading: false, error: toMessage(err) })
        return null
      }
    },
    []
  )

  useEffect(() => () => ctrlRef.current?.abort(), [])

  return { ...state, run }
}

export interface MutateState {
  /** идёт ли мутация */
  saving: boolean
  /** текст ошибки последней мутации (или null) */
  error: string | null
  /**
   * Выполнить мутацию. При ошибке выставляет error, возвращает false.
   * При успехе возвращает true (и сбрасывает error).
   */
  mutate: (fn: () => Promise<unknown>) => Promise<boolean>
  /** Сбросить ошибку (например, при открытии формы). */
  reset: () => void
}

/**
 * Обёртка над любой мутацией (post/patch/put/del) с состояниями saving/error.
 * Логику конкретного запроса передаёт вызывающая сторона:
 *   await mutate(() => api.post('/v1/tariffs', body))
 */
export function useMutate(): MutateState {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutate = useCallback(async (fn: () => Promise<unknown>): Promise<boolean> => {
    setSaving(true)
    setError(null)
    try {
      await fn()
      setSaving(false)
      return true
    } catch (err) {
      setError(toMessage(err))
      setSaving(false)
      return false
    }
  }, [])

  const reset = useCallback(() => setError(null), [])

  return { saving, error, mutate, reset }
}
